import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import PlanBadge from './common/PlanBadge';

const Card = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: var(--dark);
  font-size: 1.25rem;
`;

const UserInfoCard = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const UserAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  margin-right: 1rem;
`;

const UserDetails = styled.div`
  h4 {
    margin: 0 0 0.25rem;
    color: var(--dark);
  }
  
  p {
    margin: 0;
    color: var(--gray);
    font-size: 0.9rem;
  }
`;

const ErrorMessage = styled.div`
  color: var(--primary);
  background-color: #f8d7da;
  padding: 0.75rem;
  border-radius: 8px;
  margin: 1rem 0;
  font-size: 0.9rem;
`;

const SignOutButton = styled.button`
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--primary);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--primary);
    color: white;
  }
`;

const AccountInfoCard = () => {
  const { signOut } = useAuth();
  const { firstName, lastName, fullName, plan, email, loading, error, userData } = useUser();
  const navigate = useNavigate();
  
  // Get user's initials for avatar
  const getUserInitials = () => {
    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }
    return 'U';
  };
  
  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Account</CardTitle>
      </CardHeader>
      
      {loading ? (
        <div className="text-center">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <ErrorMessage>
          <strong>Error loading account data.</strong> 
          <p>Please try signing out and signing in again.</p>
          <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
        </ErrorMessage>
      ) : !userData ? (
        <ErrorMessage>
          <strong>Account data not found.</strong>
          <p>We couldn't find your account information. Please sign out and sign in again.</p>
          <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
        </ErrorMessage>
      ) : (
        <>
          <UserInfoCard>
            <UserAvatar>{getUserInitials()}</UserAvatar>
            <UserDetails>
              <h4>{fullName}</h4>
              <p>{email}</p>
            </UserDetails>
          </UserInfoCard>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Current Plan:</strong> {plan}
          </div>
          
          <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
        </>
      )}
    </Card>
  );
};

export default AccountInfoCard;
